import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { FileText, Users, Camera, BarChart3, CreditCard, Settings, Clock, Shield, Download, Smartphone } from 'lucide-react';
import { isAuthenticated, getLicenseStatus } from '../services/auth';

const Features = () => {
  const { darkMode } = useContext(ThemeContext);
  const [hideTrialBanner, setHideTrialBanner] = useState(isAuthenticated()); // Hide by default for authenticated users
  const [checkedLicense, setCheckedLicense] = useState(false);

  useEffect(() => {
    const checkLicenseStatus = async () => {
      if (isAuthenticated()) {
        try {
          const status = await getLicenseStatus();
          // Hide trial prompts if user has/had a trial or has a paid license
          setHideTrialBanner(status.trialLicense || status.hasPaidLicense);
        } catch (error) {
          // If we can't check license status, show the trial prompts
          console.error('Error checking license status:', error);
          setHideTrialBanner(false);
        }
      } else {
        setHideTrialBanner(false);
      }
      setCheckedLicense(true);
    };
    checkLicenseStatus();
  }, []);

  const coreFeatures = [
    {
      icon: FileText,
      title: 'Invoice Management',
      description: 'Create, customize and print professional invoices with ease.',
      details: [
        'Customizable invoice templates with your branding',
        'Automatic invoice numbering and tracking',
        'PDF generation for easy sharing and printing',
        'Payment status tracking and reminders',
        'Tax calculation and itemized billing',
        'Unlimited invoice creation'
      ]
    },
    {
      icon: Users,
      title: 'Customer Database',
      description: 'Comprehensive Customer Relationship Management for your auto shop.',
      details: [
        'Store unlimited customer records',
        'Track complete service history by customer',
        'Vehicle ownership tracking',
        'Contact information and notes',
        'Quick customer search and filtering',
        'Customer communication logs'
      ]
    },
    {
      icon: Camera,
      title: 'Vehicle Management',
      description: 'Access a comprehensive vehicle database with detailed specifications and service history for better diagnostics. Includes FREE yearly updates.',
      details: [
        'Comprehensive vehicle database with specifications',
        'VIN lookup and decoding',
        'Service history tracking per vehicle',
        'Maintenance schedule reminders',
        'FREE yearly database updates',
        'Parts and labor recommendations'
      ]
    },
    {
      icon: BarChart3,
      title: 'Reports & Analytics',
      description: 'Make data-driven decisions with powerful reporting tools.',
      details: [
        'Revenue and sales reports',
        'Customer analytics and trends',
        'Service type performance metrics',
        'Monthly, quarterly and annual summaries',
        'Export reports to PDF and Excel',
        'Custom date range reporting'
      ]
    },
    {
      icon: CreditCard,
      title: 'Payment Processing',
      description: 'Secure and flexible payment options for your customers.',
      details: [
        'Multiple payment method support',
        'Partial payment tracking',
        'Payment history logs',
        'Receipt generation',
        'Outstanding balance tracking',
        'Payment reminder system'
      ]
    },
    {
      icon: Settings,
      title: 'Customizable Settings',
      description: 'Tailor the software to match your business needs.',
      details: [
        'Custom tax rates and labor rates',
        'Company branding and logo integration',
        'Invoice template customization',
        'User permissions and roles',
        'Business information management',
        'Automated backup settings'
      ]
    }
  ];

  const additionalFeatures = [
    {
      icon: Clock,
      title: 'Time Tracking',
      description: 'Track labor hours and improve efficiency'
    },
    {
      icon: Shield,
      title: 'Data Security',
      description: 'Your data is protected with industry-standard encryption'
    },
    {
      icon: Download,
      title: 'Easy Export',
      description: 'Export data to Excel, PDF and other formats'
    },
    {
      icon: Smartphone,
      title: 'Responsive Design',
      description: 'Works seamlessly on desktop, tablet and mobile'
    }
  ];

  return (
    <div className={`flex-1 flex flex-col ${darkMode ? 'dark' : ''} bg-gray-100 dark:bg-gray-900`}>
      {/* Trial Bar - Hidden for users who have/had a trial */}
      {!hideTrialBanner && (
        <div className="bg-green-200 dark:bg-green-900/30 border-b border-green-400 dark:border-green-800">
          <div className="max-w-7xl mx-auto py-3 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center">
                <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-green-600 dark:bg-green-700">
                  <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                <div className="ml-3">
                  <p className="text-sm font-semibold text-green-950 dark:text-green-300">Free 7-Day Trial</p>
                  <p className="text-sm text-green-900 dark:text-green-400">
                    Try all features free for 7 days — no credit card required.
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0 sm:ml-4">
                <Link
                  to="/register"
                  className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-green-700 hover:bg-green-800 dark:bg-green-700 dark:hover:bg-green-600 transition-colors whitespace-nowrap"
                >
                  Start Free Trial
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="px-4 sm:px-5 lg:px-7 flex-grow">
        <div className="max-w-[1440px] mx-auto py-10">
          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Powerful Features for Your Auto Shop
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Everything you need to streamline your automotive business operations, all in one comprehensive solution.
            </p>
          </div>

          {/* Core Features - 2x3 Block Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {coreFeatures.map((feature, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-lg flex-shrink-0">
                      <feature.icon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                        {feature.title}
                      </h2>
                      <p className="text-gray-600 dark:text-gray-300 text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {feature.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700 dark:text-gray-300 text-sm">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Features Grid */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
              Additional Benefits
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {additionalFeatures.map((feature, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="bg-purple-100 dark:bg-purple-900 p-3 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section - Hidden for users who have/had a trial */}
          {!hideTrialBanner && (
            <div className="bg-blue-700 dark:bg-blue-900 rounded-xl shadow-xl p-8 text-center text-white">
              <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
              <p className="text-xl mb-6 text-blue-100">
                Start your free trial today and experience all these powerful features for yourself.
              </p>
              <Link
                to="/register"
                className="inline-block bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
              >
                Start Free Trial
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Features;